(() => {
  const COLLECTIONS = Object.freeze({
    'storm-chasers-t-shirts': 'collection:storm-chasers',
    'weather-station-owners-t-shirts': 'collection:weather-station-owners',
    'radiosonde-hunters-t-shirts': 'collection:radiosonde-hunters',
    'weather-satellite-receivers-t-shirts': 'collection:weather-satellite-receivers',
    'weather-enthusiasts-t-shirts': 'collection:weather-enthusiasts',
  });
  const PRODUCT_HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const ATTRIBUTION_PARAMETERS = new Set([
    'ad_id', 'adset_id', 'campaign_id', 'dclid', 'fbclid', 'gad_source',
    'gbraid', 'gclid', 'msclkid', 'placement', 'preview_theme_id', 'ttclid',
    'twclid', 'wbraid', '_fd', '_gl', 'pb',
  ]);
  const contextNode = document.getElementById('wm-catalog-landing-context');

  if (!contextNode) return;

  let context;
  try {
    context = JSON.parse(contextNode.textContent);
  } catch (error) {
    return;
  }

  const currentUrl = new URL(window.location.href);
  const control = {
    isCatalogClick: currentUrl.searchParams.get('wm_catalog') === '1',
    collection: currentUrl.searchParams.get('wm_collection') || '',
    item: currentUrl.searchParams.get('wm_item') || '',
    variant: currentUrl.searchParams.get('variant') || '',
  };

  const isAllowedCollection = (handle) => Object.hasOwn(COLLECTIONS, handle);
  const isValidProductHandle = (handle) =>
    handle.length > 0 && handle.length <= 255 && PRODUCT_HANDLE_PATTERN.test(handle);
  const isValidVariant = (variantIds, requestedVariant) => {
    if (!/^\d+$/.test(requestedVariant)) return false;
    const id = Number(requestedVariant);
    return Number.isSafeInteger(id) && Array.isArray(variantIds) &&
      variantIds.some((variantId) => Number(variantId) === id);
  };
  const redirectCatalogProduct = () => {
    const product = context.product;
    const collectionHandle = control.collection;
    if (!control.isCatalogClick || !isAllowedCollection(collectionHandle) ||
        !product || !isValidProductHandle(product.handle) ||
        String(product.type || product.product_type || '').trim().toLowerCase() !== 't-shirt' ||
        !Array.isArray(product.tags) ||
        !product.tags.some((tag) => String(tag).trim().toLowerCase() === COLLECTIONS[collectionHandle]) ||
        !Array.isArray(product.collections) || !product.collections.includes(collectionHandle)) return;

    const rootUrl = String(context.rootUrl || '/').replace(/\/?$/, '/');
    const destination = new URL(`${rootUrl}collections/${collectionHandle}`, currentUrl.origin);
    destination.searchParams.set('wm_item', product.handle);
    if (isValidVariant(product.variantIds, control.variant)) {
      destination.searchParams.set('variant', String(Number(control.variant)));
    }
    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (key.startsWith('utm_') || ATTRIBUTION_PARAMETERS.has(key)) {
        destination.searchParams.append(key, value);
      }
    }
    window.location.replace(destination.href);
  };
  const setLandingState = (state) => {
    document.documentElement.dataset.wmCatalogLanding = state;
  };

  const collectionLandingIsEligible = () => {
    const collectionHandle = context.collection?.handle || '';
    return (
      isAllowedCollection(collectionHandle) &&
      control.item !== '' &&
      isValidProductHandle(control.item) &&
      currentUrl.pathname.replace(/\/$/, '').endsWith(`/collections/${collectionHandle}`)
    );
  };

  if (context.pageType === 'product') {
    redirectCatalogProduct();
    return;
  }

  if (context.pageType !== 'collection' || !collectionLandingIsEligible()) return;

  setLandingState('pending');
  const pendingStyle = document.createElement('style');
  pendingStyle.textContent =
    'html[data-wm-catalog-landing="pending"] .product-grid[data-testid="product-grid"]{visibility:hidden}';
  document.head.appendChild(pendingStyle);

  const revealFallback = () => {
    setLandingState('fallback');
    pendingStyle.remove();
  };

  const getGrid = () => document.querySelector('.product-grid[data-testid="product-grid"]');
  const getDirectProductCards = (grid, productId) =>
    Array.from(grid?.children || []).filter(
      (node) => node instanceof HTMLElement && node.dataset.productId === String(productId)
    );

  const requestController = new AbortController();
  let finished = false;
  const sectionUrl = (sectionId) => {
    const path = currentUrl.pathname.replace(/\/$/, '');
    const url = new URL(`${path}/products/${encodeURIComponent(control.item)}`, currentUrl.origin);
    url.searchParams.set('section_id', sectionId);
    if (/^\d+$/.test(control.variant) && Number.isSafeInteger(Number(control.variant))) {
      url.searchParams.set('variant', String(Number(control.variant)));
    }
    // Keep theme previews on their own version; do not send ad attribution to
    // fragment requests or introduce an additional tracked page navigation.
    const preview = currentUrl.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  const fetchExactProductCard = async (sectionId) => {
    const response = await fetch(sectionUrl(sectionId), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: requestController.signal,
    });
    if (!response.ok) throw new Error('Collection section could not be loaded');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = html.querySelector('template[data-wm-catalog-card]');
    if (!(template instanceof HTMLTemplateElement) ||
        template.dataset.collection !== context.collection.handle ||
        template.dataset.productHandle !== control.item) {
      throw new Error('Product is not a public T-shirt in this exact collection');
    }
    const card = template.content.querySelector('li[data-product-id]');
    if (!card || !/^\d+$/.test(card.dataset.productId || '')) throw new Error('Product card is unavailable');
    return { card, variantIds: JSON.parse(template.dataset.variantIds || '[]') };
  };

  const applyVariantToCardLinks = (card, handle, variantIds, requestedVariant) => {
    if (!isValidVariant(variantIds, requestedVariant)) return;
    const variantId = Number(requestedVariant);

    for (const link of card.querySelectorAll('a[href]')) {
      const linkUrl = new URL(link.href, window.location.origin);
      if (linkUrl.origin !== window.location.origin || !linkUrl.pathname.endsWith(`/products/${handle}`)) continue;
      linkUrl.searchParams.set('variant', String(variantId));
      link.href = `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`;
    }
  };

  const runCollectionLanding = async () => {
    const collectionHandle = context.collection.handle;
    const resultsList = document.querySelector('results-list[section-id]');
    const grid = getGrid();
    const sectionId = resultsList?.getAttribute('section-id') || '';
    if (!grid || !sectionId) {
      throw new Error('Collection grid is unavailable');
    }
    const { card, variantIds } = await fetchExactProductCard(sectionId);
    if (finished || requestController.signal.aborted) return;
    const productId = card.dataset.productId;
    // The fragment carries selected-variant imagery. Reusing the normal grid
    // card for a variant click would keep its independently chosen palette color.
    const featuredCard = isValidVariant(variantIds, control.variant)
      ? document.importNode(card, true)
      : getDirectProductCards(grid, productId)[0] || document.importNode(card, true);
    applyVariantToCardLinks(featuredCard, control.item, variantIds, control.variant);
    featuredCard.dataset.wmCatalogFeatured = 'true';

    let scheduled = false;
    const keepFeaturedFirst = () => {
      scheduled = false;
      const currentGrid = getGrid();
      if (!currentGrid) return;

      const matches = getDirectProductCards(currentGrid, productId);
      for (const match of matches) {
        if (match !== featuredCard) match.remove();
      }
      if (featuredCard.parentElement !== currentGrid || currentGrid.firstElementChild !== featuredCard) {
        currentGrid.prepend(featuredCard);
      }
    };
    const scheduleFeaturedFirst = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(keepFeaturedFirst);
    };

    keepFeaturedFirst();
    const observer = new MutationObserver((records) => {
      // Only respond to grid insertions/replacements, not cart, header, or
      // quick-shop updates elsewhere on the page.
      if (records.some((record) => record.target === getGrid() ||
          [...record.addedNodes].some((node) => node instanceof Element &&
            (node.matches('.product-grid') || node.querySelector('.product-grid'))))) {
        scheduleFeaturedFirst();
      }
    });
    const observeGrid = () => observer.observe(resultsList.closest('.shopify-section') || resultsList, {
      childList: true, subtree: true,
    });
    observeGrid();
    window.addEventListener('pagehide', () => observer.disconnect());
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) { keepFeaturedFirst(); observeGrid(); }
    });

    finished = true;
    setLandingState('featured');
    pendingStyle.remove();
    document.dispatchEvent(
      new CustomEvent('wm:catalog-landing-ready', {
        detail: { collection: collectionHandle, handle: control.item },
      })
    );
  };

  const fallbackTimer = window.setTimeout(() => {
    finished = true;
    requestController.abort();
    revealFallback();
  }, 4000);
  const start = () => {
    if (finished) return;
    runCollectionLanding()
      .catch(revealFallback)
      .finally(() => window.clearTimeout(fallbackTimer));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
